/* eslint-disable */

/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form-main');
      this.optionSelectors = this.querySelectorAll('.option-selector, .js-option-selector');
      this.data = this.getProductData();
      this.variant = this.getSelectedVariant();
      this.selectedOptions = this.getSelectedOptions();
      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;
      this.isFeaturedProduct = !!this.dataset.featuredProduct;

      this.addEventListener('change', this.handleVariantChange.bind(this));

      // Defer these to allow for quick-add modal to full initialize
      setTimeout(() => {
        this.updateAvailability();
        this.updateAddToCartButton();
      });
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();
      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      if (this.variant) {
        this.updateMedia();
        this.updateUrl(evt);
        this.updateVariantInput();
      }

      this.updateAddToCartButton();
      this.updateAvailability();
      this.updatePrice(this.section.querySelector('.js-product-price > .price'));
      this.updateWeight();
      this.updateBarcode();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      this.updateStickyAtc();
      VariantPicker.updateLabelText(evt);

      if (!this.preSelection) {
        this.dispatchEvent(new CustomEvent('on:variant:change', {
          bubbles: true,
          detail: {
            form: this.productForm,
            variant: this.variant,
            product: this.data.product
          }
        }));
      }
    }

    /**
     * Updates the state of the sticky ATC panel
     */
    updateStickyAtc() {
      this.updatePrice(document.querySelector('.js-atc-price > .price'));

      const stickyAtcVariant = document.querySelector('.js-atc-selected-variant');
      if (stickyAtcVariant) {
        stickyAtcVariant.innerHTML = this.variant ? this.variant.title : '';
      }

      const stickyAtcVariantSwatch = document.querySelector('.js-atc-selected-variant-swatch');
      if (!stickyAtcVariantSwatch) return;

      let activeSwatch = this.querySelector('.opt-btn:checked + .opt-label--swatch') ||
        this.querySelector('.opt-btn:checked + .pdp-swatch-btn');
      if (!activeSwatch && this.variant) {
        activeSwatch = this.getSwatchElementForVariant(this.variant);
      }
      if (activeSwatch) {
        stickyAtcVariantSwatch.innerHTML = activeSwatch.innerHTML || activeSwatch.outerHTML;
      }
    }

    /**
     * Find the swatch label element that matches the current variant (for when variant is
     * changed from dropdown or URL so the checked radio may not match).
     */
    getSwatchElementForVariant(variant) {
      const selector = this.querySelector('.pdp-swatches-wrapper.option-selector');
      if (!selector) return null;
      const selectorIndex = Array.from(this.optionSelectors).indexOf(selector);
      if (selectorIndex === -1 || !variant.options || selectorIndex >= variant.options.length) return null;
      const optionValue = VariantPicker.normalizeOptionValue(variant.options[selectorIndex]);
      const inputs = selector.querySelectorAll('.opt-btn.js-option');
      const input = Array.from(inputs).find((el) => VariantPicker.normalizeOptionValue(el.value) === optionValue);
      if (!input) return null;
      const label = input.nextElementSibling;
      return label && (label.classList.contains('pdp-swatch-btn') || label.classList.contains('opt-label--swatch')) ? label : null;
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      this.productForm = this.section.querySelector('.js-product-form-main');
      if (!this.productForm) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      this.stickyAddBtn = this.stickyAddBtn || document.querySelector('.js-atc-button');

      const variantAvailable = this.variant && this.variant.available;

      this.addBtn.disabled = !variantAvailable || this.preSelection;
      if (this.stickyAddBtn) this.stickyAddBtn.disabled = !variantAvailable || this.preSelection;

      if (this.preSelection) {
        this.addBtn.textContent = theme.strings.noSelectedVariant;
        if (this.stickyAddBtn) this.stickyAddBtn.textContent = theme.strings.noSelectedVariant;
      } else {

        const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;
        this.addBtn.textContent = variantAvailable
          ? this.addBtn.dataset.addToCartText
          : unavailableStr;

        if (this.stickyAddBtn) {
          const addToCartText = variantAvailable ? this.stickyAddBtn.dataset.addToCartText : unavailableStr;
          this.stickyAddBtn.innerHTML = `
              <span class="hidden md:block">${addToCartText}</span>
              <span class="large-text align-top md:hidden">${this.stickyAddBtn.dataset.plusIcon}</span>
          `;
        }
      }
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available) {
      const el = optionEl;
      const unavailableText = exists ? theme.strings.noStock : theme.strings.noVariant;
      el.classList.toggle('is-unavailable', !available);
      el.classList.toggle('is-nonexistent', !exists);

      if (optionEl.classList.contains('custom-select__option')) {
        const customSelectBtn = optionEl.closest('.custom-select')?.querySelector('.custom-select__btn');
        const customSelectBtnValue = customSelectBtn?.textContent?.trim() ?? '';
        const optionValue = optionEl.dataset?.value?.trim() ?? '';
        if (customSelectBtnValue && optionValue && customSelectBtnValue === optionValue) {
          customSelectBtn.classList.toggle('is-unavailable', !available);
        }

        const em = el.querySelector('em');

        if (em) {
          em.hidden = available;
        }

        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute('title');
      }
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.showAvailability === 'false') return;
      const { availabilityMethod } = this.dataset; // 'downward' or 'selection'
      let currVariant = this.variant;

      if (!this.variant) {
        currVariant = { options: this.selectedOptions };
      }

      if (availabilityMethod === 'selection') {
        let selectorIndexOffset = 0;

        // Flag all options as unavailable
        this.querySelectorAll('.js-option').forEach((optionEl) => {
          VariantPicker.updateOptionAvailability(optionEl, false, false);
        });

        // Flag selector options as available or sold out, depending on the variant availability
        this.optionSelectors.forEach((selector, selectorIndex) => {
          selectorIndex = selectorIndex - selectorIndexOffset;
          if (selector.classList.contains('dual-option-selector')) {
            selectorIndexOffset = 1;
          }

          this.data.product.variants.forEach((variant) => {
            let matchCount = 0;

            variant.options.forEach((option, optionIndex) => {
              if (option === currVariant.options[optionIndex] && optionIndex !== selectorIndex) {
                matchCount += 1;
              }
            });

            if (matchCount === currVariant.options.length - 1) {
              const options = selector.querySelectorAll('.js-option');
              const optionEl = Array.from(options).find((opt) => {
                if (selector.dataset.selectorType === 'dropdown') {
                  return opt.dataset.value === variant.options[selectorIndex];
                }
                return opt.value === variant.options[selectorIndex];
              });

              if (optionEl) {
                VariantPicker.updateOptionAvailability(optionEl, true, variant.available);
              }
            }
          });
        });
      } else {
        let selectorIndexOffset = 0;

        this.optionSelectors.forEach((selector, selectorIndex) => {
          selectorIndex = selectorIndex - selectorIndexOffset;

          if (selector.classList.contains('dual-option-selector')) {
            selectorIndexOffset = 1;
          }

          const options = selector.querySelectorAll('.js-option:not([data-value=""])');
          options.forEach((option) => {
            const optionValue = selector.dataset.selectorType === 'dropdown' ? option.dataset.value : option.value;
            // any available variants with previous options and this one locked in?
            let variantsExist = false;
            let variantsAvailable = false;
            this.data.product.variants.forEach((v) => {
              let matches = 0;
              for (let i = 0; i < selectorIndex; i += 1) {
                if (v.options[i] === this.selectedOptions[i] || this.selectedOptions[i] === null) {
                  matches += 1;
                }
              }
              if (v.options[selectorIndex] === optionValue && matches === selectorIndex) {
                variantsExist = true;
                if (v.available) {
                  variantsAvailable = true;
                }
              }
            });
            VariantPicker.updateOptionAvailability(option, variantsExist, variantsAvailable);
          });
        });
      }
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === 'none') {
          const backorderProdEl = this.backorder.querySelector('.backorder__product');
          const prodTitleEl = this.section.querySelector('.product-title');
          const variantTitle = this.variant.title.includes('Default')
            ? ''
            : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the color option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest('.dual-option-selector') || evt.target.closest('.option-selector');
      if (selector.dataset.selectorType === 'dropdown') return;

      const colorText = selector.querySelectorAll('.js-color-text');
      colorText.forEach((el) => {
        el.textContent = evt.target.nextElementSibling.querySelector('.js-value').textContent;
      });
    }

    /**
     * Gets variant featured media, including Liquid-rendered fallback map.
     * @param {?object} variant
     * @returns {?object}
     */
    getVariantFeaturedMedia(variant) {
      if (!variant) return null;
      if (variant.featured_media?.id) return variant.featured_media;

      const mappedMedia = this.getVariantFeaturedMediaMap()?.[variant.id];
      if (mappedMedia?.id) return mappedMedia;

      if (variant.featured_image?.id) {
        return {
          id: variant.featured_image.id,
          position: variant.featured_image.position
        };
      }

      return null;
    }

    /**
     * Gets variant id -> featured media map rendered by Liquid.
     * @returns {?object}
     */
    getVariantFeaturedMediaMap() {
      if (typeof this.variantFeaturedMediaMap === 'undefined') {
        const mapEl = this.querySelector('.js-variant-featured-media-map');
        this.variantFeaturedMediaMap = mapEl ? JSON.parse(mapEl.textContent) : null;
      }
      return this.variantFeaturedMediaMap;
    }

    /**
     * Updates the product media.
     * Uses customSetActiveMedia when available (media-gallery); setActiveMedia is a no-op stub.
     */
    updateMedia() {
      const featuredMedia = this.getVariantFeaturedMedia(this.variant);
      if (!featuredMedia?.id) return;

      this.mediaGallery = this.mediaGallery || this.section.querySelector('media-gallery');
      if (!this.mediaGallery || this.mediaGallery.closest('.pdp-gallery')) return;

      const variantMedia = this.mediaGallery.querySelector(
        `[data-media-id="${featuredMedia.id}"]`
      );
      if (!variantMedia) return;

      if (typeof this.mediaGallery.customSetActiveMedia === 'function') {
        this.mediaGallery.customSetActiveMedia(variantMedia, true, true);
      } else {
        this.mediaGallery.setActiveMedia(variantMedia, true, true);
      }
    }



    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability =
        this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    /**
     * Updates the price.
     */
    updatePrice(priceElem) {
      if (!priceElem) return;

      let { variant } = this;
      if (this.preSelection) {
        variant = this.data.product.variants[0];
        for (let i = 1; i < this.data.product.variants.length; i += 1) {
          if (this.data.product.variants[i].price < variant.price) variant = this.data.product.variants[i];
        }
      }

      if (this.variant) {
        const priceCurrentEl = priceElem.querySelector('.price__current');
        const priceWasEl = priceElem.querySelector('.price__was');
        const unitPriceEl = priceElem.querySelector('.unit-price');
        const discountEl = priceElem.querySelector('.price__discount');
        const { price, compare_at_price: compareAtPrice } = this.variant;

        // Determine pricing display logic based on client requirements
        // Rule 1: If Final Price = Reference Price: Show only current price
        // Rule 2: If Final Price ≠ Reference Price AND Current Price < Reference Price: Show current price, struck-through reference price, and discount percentage
        // Rule 3: If Current Price ≥ Reference Price: Hide reference price completely, show only current price

        let showReferencePrice = false;
        let showDiscountPercentage = false;
        let discountPercentage = 0;

        if (compareAtPrice && compareAtPrice > 0) {
          if (price < compareAtPrice) {
            // Valid discount scenario - show reference price and percentage
            showReferencePrice = true;
            showDiscountPercentage = true;
            // Calculate discount percentage from Reference Price
            discountPercentage = Math.round(((compareAtPrice - price) / compareAtPrice) * 100);
          }
          // If price == compareAtPrice or price > compareAtPrice, show only current price
        }

        // Update price.
        if (priceCurrentEl) {
          const labelEl = priceCurrentEl.querySelector('.js-label');
          const valueEl = priceCurrentEl.querySelector('.js-value');
          if (showReferencePrice) {
            if (labelEl) labelEl.textContent = theme.strings.salePrice;
            if (valueEl) valueEl.innerHTML = this.data.formatted[variant.id].price;
            if (priceWasEl) {
              priceWasEl.querySelector('.js-label').textContent = theme.strings.regularPrice;
              priceWasEl.querySelector('.js-value').innerHTML = this.data.formatted[variant.id].compareAtPrice || '';
            }
          } else {
            if (labelEl) labelEl.textContent = theme.strings.regularPrice;
            if (valueEl) valueEl.innerHTML = this.data.formatted[variant.id].price;
            if (priceWasEl) {
              priceWasEl.querySelector('.js-label').textContent = '';
              priceWasEl.querySelector('.js-value').innerHTML = '';
            }
          }
        }

        // Update unit price, if specified.
        if (this.variant.unit_price_measurement) {
          const valueEl = priceElem.querySelector('.unit-price__price');
          const unitEl = priceElem.querySelector('.unit-price__unit');
          const value = this.variant.unit_price_measurement.reference_value;
          const unit = this.variant.unit_price_measurement.reference_unit;

          if (valueEl) valueEl.innerHTML = this.data.formatted[this.variant.id].unitPrice;
          if (unitEl) unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
        }

        if (unitPriceEl) unitPriceEl.hidden = !this.variant.unit_price_measurement;
        if (priceWasEl) priceWasEl.hidden = !showReferencePrice;
        priceElem.classList.toggle('price--on-sale', showReferencePrice);
        priceElem.classList.toggle('price--sold-out', !this.variant.available);

        if (discountEl) {
          if (showDiscountPercentage && discountPercentage > 0) {
            discountEl.textContent = `-${discountPercentage}%`;
            discountEl.hidden = false;
          } else {
            discountEl.hidden = true;
          }
        }
      } else {
        priceElem.classList.remove('price--on-sale');
        priceElem.classList.remove('price--sold-out');
      }

      const priceDefaultEl = priceElem.querySelector('.price__default');
      const priceNoVariantEl = priceElem.querySelector('.price__no-variant');
      if (priceDefaultEl) priceDefaultEl.hidden = !this.variant && !this.preSelection;
      if (priceNoVariantEl) priceNoVariantEl.hidden = this.variant || this.preSelection;
      const from = priceElem.querySelector('.price__from');
      if (from) {
        from.hidden = !this.preSelection;
      }
    }

    /**
     * Updates the weight.
     */
    updateWeight() {
      this.weights = this.weights || this.section.querySelectorAll('.product-info__weight');
      if (this.weights.length === 0) return;

      const weightAvailable = this.variant && this.variant.weight > 0;
      this.weights.forEach((weight) => {
        weight.textContent = weightAvailable ? this.data.formatted[this.variant.id].weight : '';
        weight.hidden = !weightAvailable;
      });
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcodes = this.barcodes || this.section.querySelectorAll('.product-info__barcode-value');
      if (this.barcodes.length === 0) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcodes.forEach((barcode) => {
        barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
        barcode.parentNode.hidden = !barcodeAvailable;
      });
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false' || this.isFeaturedProduct) return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.variant.id}`);
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms =
        this.forms || this.section.querySelectorAll('.js-product-form-main, .js-instalments-form');

      this.forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        input.value = this.variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /**
     * Gets the selected option element from each selector.
     * Matches original theme: only "listed" selectors contribute option values for variant match;
     * dropdowns (except dual-option-selector) push selected text.
     * @returns {Array}
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          if (!selector.classList.contains('dual-option-selector')) {
            const btn = selector.querySelector('.custom-select__btn');
            const selectedText = btn ? btn.textContent.trim() : '';
            selectedOptions.push(selectedText === this.dataset.placeholderText ? null : selectedText);
          }
        } else {
          const selected = selector.querySelector('input:checked');
          const val = selected ? selected.value : null;
          selectedOptions.push(val != null && val !== '' ? String(val).trim() : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Gets the product data.
     * @returns {?object}
     */
    getProductData() {
      const dataEl = this.querySelector('[type="application/json"]:not(.js-variant-featured-media-map)');
      const data = JSON.parse(dataEl.textContent);
      const mediaMap = this.getVariantFeaturedMediaMap();

      if (mediaMap && data.product?.variants) {
        data.product.variants.forEach((variant) => {
          if (!variant.featured_media?.id && mediaMap[variant.id]?.id) {
            variant.featured_media = mediaMap[variant.id];
          }
        });
      }

      return data;
    }



    /**
     * Normalize option value for comparison (trim, collapse spaces).
     * @param {*} val - Value from variant or input.
     * @returns {string}
     */
    static normalizeOptionValue(val) {
      if (val == null) return '';
      const s = String(val).trim();
      return s.replace(/\s+/g, ' ');
    }

    /**
     * Get selected variant data.
     * Matches original theme behaviour: match by variant.options first, then by option1/option2/option3.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      const selectedOptions = this.getSelectedOptions();
      const product = this.data && this.data.product;
      if (!product || !Array.isArray(product.variants)) return null;

      const matchByOptions = (v) => {
        if (!v.options || v.options.length !== selectedOptions.length) return false;
        return v.options.every((val, index) => VariantPicker.normalizeOptionValue(val) === VariantPicker.normalizeOptionValue(selectedOptions[index]));
      };

      let variant = product.variants.find(matchByOptions);
      if (variant) return variant;

      const matchByOptionKeys = (v) => {
        const o1 = VariantPicker.normalizeOptionValue(selectedOptions[0]);
        const o2 = selectedOptions[1] != null ? VariantPicker.normalizeOptionValue(selectedOptions[1]) : null;
        const o3 = selectedOptions[2] != null ? VariantPicker.normalizeOptionValue(selectedOptions[2]) : null;
        if (o1 && v.option1 != null && VariantPicker.normalizeOptionValue(v.option1) !== o1) return false;
        if (o2 != null && (v.option2 == null || VariantPicker.normalizeOptionValue(v.option2) !== o2)) return false;
        if (o3 != null && (v.option3 == null || VariantPicker.normalizeOptionValue(v.option3) !== o3)) return false;
        return true;
      };

      return product.variants.find(matchByOptionKeys) || null;
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
